/**
 * Mock Product Service
 * Simulates a backend API with localStorage persistence
 * Handles products, variants, images (via IndexedDB), and documents
 */

import * as imageStore from '../services/imageStore';

// ============================================================
// Storage Keys
// ============================================================
const STORAGE_KEYS = {
  PRODUCTS: 'geppro_products',
  VARIANTS: 'geppro_variants',
};

// ============================================================
// Mock Data
// ============================================================
const MOCK_CNAES = [
  { code: '1011-2/01', description: 'Abate de bovinos' },
  { code: '1011-2/02', description: 'Abate de suínos' },
  { code: '4729-6/99', description: 'Comércio varejista de alimentos' },
];

const MOCK_DOCUMENTS = [
  { id: 1, name: 'Manual de Uso' },
  { id: 2, name: 'Certificado de Garantia' },
  { id: 3, name: 'Ficha Técnica' },
  { id: 4, name: 'Guia de Instalação' },
  { id: 5, name: 'Documento Fiscal' },
  { id: 6, name: 'Termo de Responsabilidade' },
  { id: 7, name: 'Comprovante Ambiental' },
  { id: 8, name: 'Folha de Especificações' },
  { id: 9, name: 'Manual Avançado' },
  { id: 10, name: 'Guia de Segurança' },
];

// ============================================================
// Storage Helpers
// ============================================================
function loadFromStorage(key, defaultValue = []) {
  try {
    const json = localStorage.getItem(key);
    return json ? JSON.parse(json) : defaultValue;
  } catch {
    return defaultValue;
  }
}

function saveToStorage(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (err) {
    console.error(`Failed to save to ${key}:`, err);
  }
}

function paginate(array, page = 1, perPage = 5) {
  const start = (page - 1) * perPage;
  return array.slice(start, start + perPage);
}

// ============================================================
// In-Memory Database
// ============================================================
const db = {
  products: loadFromStorage(STORAGE_KEYS.PRODUCTS, []),
  variants: loadFromStorage(STORAGE_KEYS.VARIANTS, []),
};

// ============================================================
// Image Serialization Helpers
// ============================================================
async function serializeImage(imageObj) {
  if (!imageObj) return null;
  
  // If it's a File object, save to IndexedDB
  if (imageObj.file instanceof File) {
    const key = await imageStore.saveImage(imageObj.file);
    return { key };
  }
  
  // If it already has a key, keep it
  if (imageObj.key) {
    return { key: imageObj.key };
  }
  
  // If it's just a URL string, store it (legacy support)
  if (typeof imageObj === 'string') {
    return { url: imageObj };
  }
  
  return null;
}

async function serializeVariantImages(variant) {
  if (!variant) return null;

  const mainImage = await serializeImage(variant.mainImage);
  
  const galleryImages = [];
  if (Array.isArray(variant.galleryImages)) {
    for (const img of variant.galleryImages) {
      const serialized = await serializeImage(img);
      if (serialized) galleryImages.push(serialized);
    }
  }

  return {
    ...variant,
    mainImage,
    galleryImages,
  };
}

// ============================================================
// Service Methods
// ============================================================
const mockProductService = {

  /**
   * Fetch available filters for products
   * Categories are loaded from local category service
   */
  async fetchProductFilters() {
    return new Promise(resolve => {
      setTimeout(() => {
        // Load categories from localStorage
        const categories = loadFromStorage('geppro_categories', []);
        const categoryNames = categories
          .filter(c => c.itemType === 'Mercadoria')
          .map(c => c.name);

        resolve({
          category: categoryNames.length > 0 ? categoryNames : ['Sem categorias'],
          status: ['Ativo', 'Inativo'],
          tags: ['Premium', 'Orgânico', 'Importado'],
        });
      }, 200);
    });
  },

  /**
   * Search products with optional filters
   */
  async searchProducts(params = {}) {
    return new Promise(resolve => {
      setTimeout(() => {
        try {
          let items = [...db.products];

          // Text search
          if (params.q && typeof params.q === 'string' && params.q.trim()) {
            const query = params.q.toLowerCase();
            items = items.filter(p =>
              p.title?.toLowerCase().includes(query) ||
              p.description?.toLowerCase().includes(query)
            );
          }

          // Category filter (supports string "a,b" or array [a,b])
          let categories = [];
          const catParam = params.category;
          if (typeof catParam === 'string' && catParam.trim()) {
            categories = catParam.split(',');
          } else if (Array.isArray(catParam) && catParam.length) {
            categories = catParam;
          }
          if (categories.length) {
            items = items.filter(p => categories.includes(p.category));
          }

          resolve({ items, total: items.length });
        } catch (err) {
          console.error('searchProducts internal error:', err);
          resolve({ items: [], total: 0 });
        }
      }, 200);
    });
  },

  /**
   * Fetch a single product by ID with its variants
   */
  async fetchProductById(id) {
    return new Promise(resolve => {
      setTimeout(() => {
        const product = db.products.find(p => p.id === id);
        if (!product) {
          resolve(null);
          return;
        }

        const variants = db.variants.filter(v => v.productId === id);

        resolve({
          ...product,
          variants,
        });
      }, 200);
    });
  },

  /**
   * Fetch CNAEs for fiscal data
   */
  async fetchCNAEs() {
    return new Promise(resolve => {
      setTimeout(() => resolve(MOCK_CNAES), 150);
    });
  },

  /**
   * Fetch documents (paginated)
   */
  async fetchDocuments(page = 1, perPage = 5) {
    return new Promise(resolve => {
      setTimeout(() => {
        const paginated = paginate(MOCK_DOCUMENTS, page, perPage);
        resolve(paginated);
      }, 200);
    });
  },

  /**
   * Search documents by name
   */
  async searchDocuments(term = '') {
    return new Promise(resolve => {
      setTimeout(() => {
        const filtered = MOCK_DOCUMENTS.filter(d =>
          d.name.toLowerCase().includes(term.toLowerCase())
        );
        resolve(filtered);
      }, 150);
    });
  },

  /**
   * Create a new product with variants
   */
  async createProduct(payload) {
    return new Promise(async resolve => {
      try {
        const { variants, defaultVariantIndex, ...productData } = payload;

        // Serialize variant images
        const serializedVariants = [];
        if (Array.isArray(variants)) {
          for (const variant of variants) {
            const serialized = await serializeVariantImages(variant);
            serializedVariants.push(serialized);
          }
        }

        // Determine product main image from default variant
        let mainImage = null;
        const defaultIndex = typeof defaultVariantIndex === 'number' ? defaultVariantIndex : 0;
        if (serializedVariants[defaultIndex]?.mainImage) {
          mainImage = serializedVariants[defaultIndex].mainImage;
        }

        // Create product
        const newId = db.products.length > 0 
          ? Math.max(...db.products.map(p => p.id)) + 1 
          : 1;

        const newProduct = {
          id: newId,
          ...productData,
          image: mainImage,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        db.products.push(newProduct);
        saveToStorage(STORAGE_KEYS.PRODUCTS, db.products);

        // Create variants linked to product
        if (serializedVariants.length > 0) {
          const baseVariantId = db.variants.length > 0
            ? Math.max(...db.variants.map(v => v.id)) + 1
            : 1;

          const newVariants = serializedVariants.map((variant, idx) => ({
            id: baseVariantId + idx,
            productId: newId,
            ...variant,
            createdAt: new Date().toISOString(),
          }));

          db.variants.push(...newVariants);
          saveToStorage(STORAGE_KEYS.VARIANTS, db.variants);
        }

        resolve(newProduct);
      } catch (err) {
        console.error('createProduct error:', err);
        resolve(null);
      }
    });
  },

  /**
   * Delete a product and its variants
   */
  async deleteProduct(id) {
    return new Promise(async resolve => {
      try {
        const product = db.products.find(p => p.id === id);
        if (!product) {
          resolve({ success: false });
          return;
        }

        // Clean up product image
        if (product.image?.key) {
          try { await imageStore.deleteImage(product.image.key); } catch {}
        }

        // Clean up variant images
        const variants = db.variants.filter(v => v.productId === id);
        for (const variant of variants) {
          if (variant.mainImage?.key) {
            try { await imageStore.deleteImage(variant.mainImage.key); } catch {}
          }
          if (Array.isArray(variant.galleryImages)) {
            for (const img of variant.galleryImages) {
              if (img?.key) {
                try { await imageStore.deleteImage(img.key); } catch {}
              }
            }
          }
        }

        // Remove from database
        db.products = db.products.filter(p => p.id !== id);
        db.variants = db.variants.filter(v => v.productId !== id);
        
        saveToStorage(STORAGE_KEYS.PRODUCTS, db.products);
        saveToStorage(STORAGE_KEYS.VARIANTS, db.variants);

        resolve({ success: true });
      } catch (err) {
        console.error('deleteProduct error:', err);
        resolve({ success: false, error: err.message });
      }
    });
  },

  /**
   * Update an existing product
   */
  async updateProduct(id, payload) {
    return new Promise(async resolve => {
      try {
        const index = db.products.findIndex(p => p.id === id);
        if (index === -1) {
          resolve(null);
          return;
        }

        const { variants, defaultVariantIndex, ...productData } = payload;

        // Serialize variant images
        const serializedVariants = [];
        if (Array.isArray(variants)) {
          for (const variant of variants) {
            const serialized = await serializeVariantImages(variant);
            serializedVariants.push(serialized);
          }
        }

        // Collect image keys that should be retained (present in new variants)
        const retainKeys = new Set();
        for (const v of serializedVariants) {
          if (v?.mainImage?.key) retainKeys.add(v.mainImage.key);
          if (Array.isArray(v?.galleryImages)) {
            for (const img of v.galleryImages) {
              if (img?.key) retainKeys.add(img.key);
            }
          }
        }

        // Update product main image from default variant
        let mainImage = db.products[index].image;
        const defaultIndex = typeof defaultVariantIndex === 'number' ? defaultVariantIndex : 0;
        if (serializedVariants[defaultIndex]?.mainImage) {
          mainImage = serializedVariants[defaultIndex].mainImage;
        }

        // Update product
        db.products[index] = {
          ...db.products[index],
          ...productData,
          image: mainImage,
          updatedAt: new Date().toISOString(),
        };
        saveToStorage(STORAGE_KEYS.PRODUCTS, db.products);

        // Remove old variants and their images
        const oldVariants = db.variants.filter(v => v.productId === id);
        for (const variant of oldVariants) {
          // Clean up old images ONLY if not retained in new variants
          if (variant.mainImage?.key && !retainKeys.has(variant.mainImage.key)) {
            try { await imageStore.deleteImage(variant.mainImage.key); } catch {}
          }
          if (Array.isArray(variant.galleryImages)) {
            for (const img of variant.galleryImages) {
              if (img?.key && !retainKeys.has(img.key)) {
                try { await imageStore.deleteImage(img.key); } catch {}
              }
            }
          }
        }

        // Remove old variants
        db.variants = db.variants.filter(v => v.productId !== id);

        // Add new variants
        if (serializedVariants.length > 0) {
          const baseVariantId = db.variants.length > 0
            ? Math.max(...db.variants.map(v => v.id)) + 1
            : 1;

          const newVariants = serializedVariants.map((variant, idx) => ({
            id: baseVariantId + idx,
            productId: id,
            ...variant,
            updatedAt: new Date().toISOString(),
          }));

          db.variants.push(...newVariants);
          saveToStorage(STORAGE_KEYS.VARIANTS, db.variants);
        }

        resolve(db.products[index]);
      } catch (err) {
        console.error('updateProduct error:', err);
        resolve(null);
      }
    });
  },
};

export default mockProductService;




